'use es6';

import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UICardSection from 'UIComponents/card/UICardSection';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { getLongDisplayName } from 'MeetingsLib/utils/utils';
import ProfileHeader from 'MeetingsLib/components/header/ProfileHeader';
import FreemiumUpgradeButton from 'MeetingsLib/components/freemium/FreemiumUpgradeButton';
import { track } from 'MeetingsLib/tracking/UsageTracker';
import Branding from 'MeetingsBase/models/Branding';
import UserProfile from 'MeetingsBase/models/UserProfile';
import getFreemiumViralLink from 'MeetingsLib/components/freemium/getFreemiumViralLink';
import { THANKYOU_VIRAL_HUBS_CTA } from 'MeetingsLib/constants/paths';
import { redirectToUrl } from 'MeetingsLib/utils/utils';
import { SECOND, REDIRECT_DELAY_IN_SECONDS } from 'MeetingsLib/constants/time';
import H4 from 'UIComponents/elements/headings/H4';
import { useCallback, useContext, useEffect, useState } from 'react';
import PreviewContext from '../../utils/PreviewContext';

function Advert(_ref) {
  var portalId = _ref.portalId,
      embedded = _ref.embedded,
      isSalesStarter = _ref.isSalesStarter,
      branding = _ref.branding;
  var goToMeetingsPage = useCallback(function () {
    track('meetingsLibClickPromoLink', {
      page: 'thank you'
    });
    var thankYouViralLink = getFreemiumViralLink(THANKYOU_VIRAL_HUBS_CTA, portalId, embedded);
    window.open(thankYouViralLink, '_blank');
  }, [portalId, embedded]);

  if (isSalesStarter) {
    return null;
  }

  return /*#__PURE__*/_jsx(UIFlex, {
    align: "center",
    justify: "center",
    children: /*#__PURE__*/_jsx(FreemiumUpgradeButton, {
      handleButtonClick: goToMeetingsPage,
      branding: branding
    })
  });
}

function RedirectCountdown(_ref2) {
  var willRedirect = _ref2.willRedirect,
      timeTillRedirect = _ref2.timeTillRedirect;

  if (willRedirect) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      className: "p-top-4",
      layout: "centered",
      showLabel: true,
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "summary.redirect",
        options: {
          seconds: timeTillRedirect
        }
      })
    });
  }

  return null;
}

export default function BookingSummary(_ref3) {
  var bookedDateTime = _ref3.bookedDateTime,
      branding = _ref3.branding,
      embedded = _ref3.embedded,
      isSalesStarter = _ref3.isSalesStarter,
      offline = _ref3.offline,
      profile = _ref3.profile,
      userTimezone = _ref3.userTimezone,
      onSuccess = _ref3.onSuccess,
      redirectUrl = _ref3.redirectUrl,
      allowRedirect = _ref3.allowRedirect,
      portalId = _ref3.portalId;
  var isPreview = useContext(PreviewContext);

  var _useState = useState(REDIRECT_DELAY_IN_SECONDS),
      _useState2 = _slicedToArray(_useState, 2),
      timeTillRedirect = _useState2[0],
      setTimeTillRedirect = _useState2[1];

  var willRedirect = redirectUrl && isSalesStarter && allowRedirect;
  var updateTimeTillRedirect = useCallback(function () {
    setTimeTillRedirect(function (_timeTillRedirect) {
      return _timeTillRedirect > 1 ? _timeTillRedirect - 1 : 1;
    });
  }, [setTimeTillRedirect]);
  var redirectToCustomPage = useCallback(function () {
    redirectToUrl(redirectUrl, embedded);
  }, [redirectUrl, embedded]);
  useEffect(function () {
    var redirectInterval;
    var redirectTimeout;
    onSuccess(); // check isPreview here because we want to show the redirect UI in the
    // preview, but not actually start any timers

    if (willRedirect && !isPreview) {
      redirectInterval = window.setInterval(updateTimeTillRedirect, SECOND);
      redirectTimeout = window.setTimeout(redirectToCustomPage, REDIRECT_DELAY_IN_SECONDS * SECOND);
    }

    return function () {
      if (redirectTimeout) {
        window.clearTimeout(redirectTimeout);
      }

      if (redirectInterval) {
        window.clearInterval(redirectInterval);
      }
    };
  }, [onSuccess, willRedirect, isPreview, updateTimeTillRedirect, redirectToCustomPage]);
  var classes = 'book-summary m-x-auto' + (!embedded ? " p-top-12" : "");
  var shouldRenderDateTime = !willRedirect;
  var suffix = offline ? 'offline' : 'online';
  var redirect = willRedirect ? 'redirect.' : '';
  var preMessageKey = "summary.tagline." + redirect + suffix;
  return /*#__PURE__*/_jsx("div", {
    className: classes,
    "data-timestamp": bookedDateTime,
    children: /*#__PURE__*/_jsx(UICardWrapper, {
      className: "width-100 p-all-0 height-100",
      children: /*#__PURE__*/_jsx(UICardSection, {
        className: "p-all-0 height-100",
        children: /*#__PURE__*/_jsxs("div", {
          className: "m-x-4 p-y-11 p-x-3 text-center",
          children: [/*#__PURE__*/_jsx(ProfileHeader, {
            booked: true,
            offline: offline,
            profile: profile
          }), /*#__PURE__*/_jsx("p", {
            className: "text-center m-top-3",
            children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
              message: preMessageKey,
              options: {
                fullName: getLongDisplayName(profile)
              }
            })
          }), shouldRenderDateTime && /*#__PURE__*/_jsxs(H4, {
            className: "m-bottom-1 m-top-5",
            children: [I18n.moment(bookedDateTime).tz(userTimezone).format('LL'), /*#__PURE__*/_jsx("br", {}), I18n.moment(bookedDateTime).tz(userTimezone).format('LT')]
          }), /*#__PURE__*/_jsx(Advert, {
            portalId: portalId,
            embedded: embedded,
            branding: branding,
            isSalesStarter: isSalesStarter
          }), /*#__PURE__*/_jsx(RedirectCountdown, {
            willRedirect: willRedirect,
            timeTillRedirect: timeTillRedirect
          })]
        })
      })
    })
  });
}
BookingSummary.propTypes = {
  bookedDateTime: PropTypes.number.isRequired,
  branding: PropTypes.instanceOf(Branding).isRequired,
  embedded: PropTypes.bool.isRequired,
  isSalesStarter: PropTypes.bool,
  offline: PropTypes.bool,
  profile: PropTypes.instanceOf(UserProfile),
  userTimezone: PropTypes.oneOf(I18n.moment.tz.names()),
  onSuccess: PropTypes.func.isRequired,
  redirectUrl: PropTypes.string,
  allowRedirect: PropTypes.bool,
  portalId: PropTypes.number
};